import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import InstallerLayout from '../components/installer/InstallerLayout';
import PhotoUploader from '../components/installer/PhotoUploader';
import { formatShortDate } from '../utils/date-utils';
import { useJobs } from '../hooks/useJobs';
import { useAuth } from '../contexts/AuthContext';
import CompleteJobForm from '../components/installer/CompleteJobForm';
import ReportIssueForm from '../components/installer/ReportIssueForm';
import AddNoteForm from '../components/installer/AddNoteForm';
import MarkIncompleteForm from '../components/installer/MarkIncompleteForm';

export default function InstallerJobDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const job = location.state?.job;
  const { refreshJobs } = useJobs();
  const { currentUser } = useAuth();
  
  const [showPhotoUploader, setShowPhotoUploader] = useState(false);
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showMarkIncomplete, setShowMarkIncomplete] = useState(false);
  const [photoType, setPhotoType] = useState('before');

  if (!job) {
    navigate('/installer');
    return null;
  }

  const installerName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Installer';

  const handlePhotoUploadComplete = async () => {
    if (refreshJobs) {
      await refreshJobs();
    }
  };

  const handleMarkIncompleteSuccess = () => {
    alert('✅ Office has been notified!');
    if (refreshJobs) {
      refreshJobs();
    }
  };

  const ActionButton = ({ icon, label, onClick, color = '#8B4513' }) => (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        minHeight: '56px',
        padding: '16px',
        background: 'white',
        border: `2px solid ${color}`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '16px',
        fontWeight: '600',
        color: color,
        cursor: 'pointer',
        transition: 'all 0.2s',
        marginBottom: '12px'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.02)';
        e.currentTarget.style.background = `${color}10`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.background = 'white';
      }}
    >
      <span style={{ fontSize: '24px' }}>{icon}</span>
      <span>{label}</span>
    </button>
  );

  return (
    <InstallerLayout>
      <button
        onClick={() => navigate('/installer')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          background: 'none',
          border: 'none',
          color: '#8B4513',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          marginBottom: '20px',
          padding: '8px 0',
          minHeight: '44px'
        }}
      >
        <span style={{ fontSize: '20px' }}>←</span>
        <span>Back</span>
      </button>

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#8B4513',
          marginBottom: '16px'
        }}>
          #{job.contractNumber} - {job.customerName || job.name}
        </div>

        {job._dayInfo && (
          <div style={{
            display: 'inline-block',
            fontSize: '13px',
            fontWeight: '600',
            color: '#f97316',
            background: 'rgba(249, 115, 22, 0.1)',
            padding: '6px 12px',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            {job._dayInfo}
          </div>
        )}

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          fontSize: '15px',
          color: '#2b2b2b',
          lineHeight: '1.6'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>📍</span>
            <div>
              <div style={{ fontWeight: '600' }}>{job.address}</div>
              {job.city && job.state && (
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {job.city}, {job.state} {job.zip || ''}
                </div>
              )}
            </div>
          </div>

          {job.customerPhone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>☎️</span>
              <a 
                href={`tel:${job.customerPhone}`}
                style={{
                  color: '#8B4513',
                  textDecoration: 'none',
                  fontWeight: '600'
                }}
              >
                {job.customerPhone}
              </a>
            </div>
          )}

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
            marginTop: '8px'
          }}>
            {job.windowCount > 0 && (
              <div style={{
                padding: '12px',
                background: '#f5f2ed',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🪟</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {job.windowCount} window{job.windowCount !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            {job.doorCount > 0 && (
              <div style={{
                padding: '12px',
                background: '#f5f2ed',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🚪</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  {job.doorCount} door{job.doorCount !== 1 ? 's' : ''}
                </div>
              </div>
            )}

            <div style={{
              padding: '12px',
              background: '#f5f2ed',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>🕐</div>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>
                {job.durationDays || 1} day{(job.durationDays || 1) !== 1 ? 's' : ''}
              </div>
            </div>

            <div style={{
              padding: '12px',
              background: '#f5f2ed',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '24px', marginBottom: '4px' }}>📅</div>
              <div style={{ fontSize: '14px', fontWeight: '600' }}>
                {formatShortDate(job.scheduledDate)}
              </div>
            </div>
          </div>
        </div>

        {job.notes && (
          <div style={{
            marginTop: '20px',
            padding: '16px',
            background: 'rgba(59, 130, 246, 0.05)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            borderRadius: '12px'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              color: '#2b2b2b',
              marginBottom: '8px'
            }}>
              📝 Manager Notes:
            </div>
            <div style={{
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap'
            }}>
              "{job.notes}"
            </div>
          </div>
        )}
      </div>

      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#2b2b2b',
          marginBottom: '16px'
        }}>
          Actions
        </div>

        <ActionButton
          icon="📸"
          label="Upload Photos"
          onClick={() => {
            setPhotoType('before');
            setShowPhotoUploader(true);
          }}
          color="#3b82f6"
        />

        <ActionButton
          icon="📝"
          label="Add Note"
          onClick={() => setShowAddNote(true)}
          color="#8B4513"
        />

        <ActionButton
          icon="⏰"
          label="Mark Incomplete - Need to Return"
          onClick={() => setShowMarkIncomplete(true)}
          color="#f59e0b"
        />

        <ActionButton
          icon="⚠️"
          label="Report Issue"
          onClick={() => setShowReportIssue(true)}
          color="#f97316"
        />

        <ActionButton
          icon="✅"
          label="Complete Job"
          onClick={() => setShowCompleteForm(true)}
          color="#22c55e"
        />
      </div>

      {job.installerPhotos && job.installerPhotos.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#2b2b2b',
            marginBottom: '16px'
          }}>
            📸 Uploaded Photos
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
          }}>
            {job.installerPhotos.map((photo, index) => (
              <div key={index} style={{
                position: 'relative',
                paddingBottom: '100%',
                background: '#f5f2ed',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <img
                  src={photo.url}
                  alt={`${photo.type} photo`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  padding: '8px',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {photo.type === 'before' ? '📷 Before' : photo.type === 'after' ? '📷 After' : '⚠️ Issue'}
                  <div style={{ fontSize: '10px', opacity: 0.9 }}>
                    by {photo.uploadedBy}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {job.installerNotes && job.installerNotes.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            color: '#2b2b2b',
            marginBottom: '16px'
          }}>
            📝 Team Notes
          </div>

          {job.installerNotes.map((note, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                background: '#f5f2ed',
                borderRadius: '12px',
                marginBottom: '12px'
              }}
            >
              <div style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#8B4513',
                marginBottom: '4px'
              }}>
                {note.addedBy}
              </div>
              <div style={{
                fontSize: '14px',
                color: '#2b2b2b',
                marginBottom: '4px',
                lineHeight: '1.5'
              }}>
                {note.text}
              </div>
              <div style={{
                fontSize: '11px',
                color: '#999'
              }}>
                {note.timestamp?.toDate?.().toLocaleString() || 'Just now'}
              </div>
            </div>
          ))}
        </div>
      )}

      {showPhotoUploader && (
        <PhotoUploader
          isOpen={showPhotoUploader}
          onClose={() => setShowPhotoUploader(false)}
          jobId={job.id}
          photoType={photoType}
          onUploadComplete={handlePhotoUploadComplete}
        />
      )}

      {showReportIssue && (
        <ReportIssueForm
          isOpen={showReportIssue}
          onClose={() => setShowReportIssue(false)}
          job={job}
        />
      )}

      {showAddNote && (
        <AddNoteForm
          isOpen={showAddNote}
          onClose={() => setShowAddNote(false)}
          job={job}
        />
      )}

      {showCompleteForm && (
        <CompleteJobForm
          isOpen={showCompleteForm}
          onClose={() => setShowCompleteForm(false)}
          job={job}
        />
      )}

      {showMarkIncomplete && (
        <MarkIncompleteForm
          isOpen={showMarkIncomplete}
          onClose={() => setShowMarkIncomplete(false)}
          job={job}
          installerName={installerName}
          onSuccess={handleMarkIncompleteSuccess}
        />
      )}
    </InstallerLayout>
  );
}